/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'

export const fetchMyAlerts = createAsyncThunk('fetchMyAlerts', async () => {
  const jwtToken = localStorage.getItem('myrJwtToken')
  const rcpid = localStorage.getItem('rutgersEduRCPID')
  const res = await axios.all([
    axios.get(process.env.REACT_APP_MYALERTS_ENDPOINT, {
      headers: {
        JWT: jwtToken,
        XWT: rcpid,
      },
    }),
    axios.get(
      `${process.env.REACT_APP_MYALERTSHISTORY_ENDPOINT}${
        window.location.hostname === 'localhost' ? '' : `?rcpid=${rcpid}`
      }`,
      {
        headers: {
          JWT: jwtToken,
          XWT: rcpid,
        },
      },
    ),
  ])
  // eslint-disable-next-line no-unused-vars
  const personalAlerts = [
    ...(Array.isArray(res[0].data?.personalAlerts)
      ? res[0].data?.personalAlerts
      : []),
    ...(Array.isArray(res[0].data?.publishedAlerts)
      ? res[0].data?.publishedAlerts
      : []),
  ]
  const alertHistory = [
    ...(Array.isArray(res[1].data?.personalAlerts)
      ? res[1].data?.personalAlerts
      : []),
    ...(Array.isArray(res[1].data?.publishedAlerts)
      ? res[1].data?.publishedAlerts
      : []),
    ...(Array.isArray(res[1].data?.itAlerts) ? res[1].data?.itAlerts : []),
    ...(Array.isArray(res[1].data?.refundAlerts)
      ? res[1].data?.refundAlerts
      : []),
  ]
  return {
    emergencyAlerts: [
      ...(Array.isArray(res[0]?.data?.emergencyAlerts)
        ? res[0].data?.emergencyAlerts
        : []),
    ],
    personalAlerts,
    notificationAlerts: [
      ...(Array.isArray(res[0]?.data?.emergencyAlerts)
        ? res[0].data?.emergencyAlerts
        : []),
      ...(Array.isArray(res[0]?.data?.personalAlerts)
        ? res[0].data.personalAlerts
        : []),
      ...(Array.isArray(res[0]?.data?.publishedAlerts)
        ? res[0].data.publishedAlerts.filter(
            (publishedAlert) => publishedAlert.severityLevel !== 'low',
          )
        : []),
      ...(Array.isArray(res[0]?.data?.refundAlerts)
        ? res[0].data.refundAlerts
        : []),
      ...(Array.isArray(res[0]?.data?.customNotifications)
        ? res[0].data.customNotifications
        : []),
    ].sort((a, b) => (a?.acknowledgedTS ? 1 : 0) - (b?.acknowledgedTS ? 1 : 0)),
    itAlerts: res[0].data?.itAlerts,
    alertHistory: alertHistory?.sort((a, b) => {
      // Function to extract the timestamp from a given key or return null if the key is not found
      const extractTimestamp = (obj) =>
        obj.start_date || obj.sent_date || obj.lastUpdated

      // Extract timestamps from the objects
      const timestampA = extractTimestamp(a)
      const timestampB = extractTimestamp(b)

      // Compare timestamps as strings in reverse order
      return timestampB.localeCompare(timestampA)
    }),
    refundAlerts: res[0].data?.refundAlerts,
    customNotifications: res[0]?.data?.customNotifications,
  }
})

export const acknowledgeAlert = createAsyncThunk(
  'acknowledgeAlert',
  async (alert) => {
    const jwtToken = localStorage.getItem('myrJwtToken')
    const rcpid = localStorage.getItem('rutgersEduRCPID')
    if (window.location.hostname !== 'localhost') {
      const { dockey } = alert
      await axios.post(
        `${process.env.REACT_APP_MYALERTS_ACKNOWLEDGE}?dockey=${dockey}`,
        {},
        {
          headers: {
            JWT: jwtToken,
            XWT: rcpid,
          },
        },
      )
    }
    let url = process.env.REACT_APP_MYALERTS_ENDPOINT
    if (window.location.hostname === 'localhost') {
      url = process.env.REACT_APP_MYALERTS_POST_ENDPOINT
    }
    const res = await axios.get(url, {
      headers: {
        JWT: jwtToken,
        XWT: rcpid,
      },
    })
    return res.data
  },
)

export const fetchPortalAlerts = createAsyncThunk(
  'fetchPortalAlerts',
  async () => {
    const jwtToken = localStorage.getItem('myrJwtToken')
    const res = await axios.get(
      `${process.env.REACT_APP_PORTALALERT_ENDPOINT}`,
      {
        headers: {
          token: jwtToken,
        },
      },
    )
    return res.data
  },
)

export const acknowledgePortalAlerts = createAsyncThunk(
  'acknowledgePortalAlerts',
  async (universityAlertId) => {
    const jwtToken = localStorage.getItem('myrJwtToken')
    const res = await axios.post(
      `${process.env.REACT_APP_PORTALALERT_ACKNOWLEDGE_ENDPOINT}`,
      {
        universityAlertId,
      },
      {
        headers: {
          token: jwtToken,
        },
      },
    )
    return res.data
  },
)

const initialState = {
  status: 'idle',
  notificationAlertsStatus: 'idle',
  alertTitle: 'Alert Center',
  data: {
    title: 'My Alerts',
    categories: ['Active', 'History'],
    alerts: [],
    notificationAlerts: [],
  },
  ref: false,
  loaded: false,
  refresh: false,
  notificationsDialogOpen: false,
  alertIndex: 0,
  mainTabValue: 0,
  secondaryTabValue: 0,
  snackbarOpen: true,
  alertsHistoryOpen: false,
}

const myAlertsSlice = createSlice({
  name: 'myAlerts',
  initialState,
  reducers: {
    focusAlert: (state) => {
      state.ref = !state.ref
    },
    resetRefresh: (state) => {
      state.refresh = false
    },
    updateNotificationsDialogOpen: (state) => {
      state.notificationsDialogOpen = !state.notificationsDialogOpen
    },
    updateAlertIndex: (state, action) => {
      state.alertIndex = action.payload
    },
    updateSnackbarOpen: (state, action) => {
      state.snackbarOpen = action.payload
    },
    updateAlertsHistoryOpen: (state) => {
      state.alertsHistoryOpen = !state.alertsHistoryOpen
    },
    updateAlertTitle: (state, action) => {
      state.alertTitle = action.payload
      if (action.payload === 'Notification Center') {
        state.mainTabValue = 1
      } else {
        state.mainTabValue = 0
      }
    },
    updateMainTabValue: (state, action) => {
      state.mainTabValue = action.payload
    },
    updateSecondaryTabValue: (state, action) => {
      state.secondaryTabValue = action.payload
    },
  },
  extraReducers: {
    [fetchMyAlerts.rejected]: (state) => {
      state.status = 'error'
    },
    [fetchMyAlerts.pending]: (state) => {
      state.status = 'pending'
    },
    [fetchMyAlerts.fulfilled]: (state, action) => {
      state.status = 'fulfilled'
      // categories no longer returned from API so merge with initialState
      state.data.alerts = action.payload
    },
    [acknowledgeAlert.fulfilled]: (state, action) => {
      const { dockey } = action.meta.arg || {}
      const now = new Date().toISOString()
      const markAcknowledged = (alert) =>
        alert?.dockey === dockey ? { ...alert, acknowledgedTS: now } : alert

      const alertCategoryKeys = [
        'emergencyAlerts',
        'personalAlerts',
        'itAlerts',
        'refundAlerts',
        'customNotifications',
        'notificationAlerts',
      ]
      alertCategoryKeys.forEach((key) => {
        if (Array.isArray(state.data.alerts?.[key])) {
          state.data.alerts[key] = state.data.alerts[key].map(markAcknowledged)
        }
      })

      // state.alertIndex = 0
      state.alertAcknowledgeStatus = 'fulfilled'
    },
    [fetchPortalAlerts.rejected]: (state) => {
      state.notificationAlertsStatus = 'error'
    },
    [fetchPortalAlerts.pending]: (state) => {
      state.notificationAlertsStatus = 'pending'
    },
    [fetchPortalAlerts.fulfilled]: (state, action) => {
      state.notificationAlertsStatus = 'fulfilled'
      const portalAlerts = Array.isArray(action.payload) ? action.payload : []

      // Announcements and Events are surfaced as notifications
      const notifications = portalAlerts.filter(
        (pAlert) =>
          pAlert?.typeCode === 'Announcement' || pAlert?.typeCode === 'Event',
      )
      state.data.notificationAlerts = notifications
      state.data.activeNotificationList = notifications.filter(
        (nAlert) => nAlert?.acknowledgements?.length === 0,
      )
      state.data.historyNotificationList = notifications.filter(
        (nAlert) => nAlert?.acknowledgements?.length > 0,
      )

      // Everything else (e.g. typeCode "Alert") belongs in the alerts array.
      // Map acknowledgements -> acknowledgedTS so these items flow through
      // AlertGUI's active/history split like the other alerts.
      const portalPersonalAlerts = portalAlerts
        .filter(
          (pAlert) =>
            pAlert?.typeCode !== 'Announcement' && pAlert?.typeCode !== 'Event',
        )
        .map((pAlert) => {
          const ack = pAlert?.acknowledgements?.[0]
          return {
            ...pAlert,
            source: 'Notification',
            dockey: pAlert?.id,
            alerttype: pAlert?.typeCode,
            severityLevel: pAlert?.severityCode,
            sent_date: pAlert?.startDate,
            message: pAlert?.description,
            acknowledgedTS:
              pAlert?.acknowledgements?.length > 0
                ? ack?.acknowledgedTS ||
                  ack?.timestamp ||
                  ack?.dateAcknowledged ||
                  pAlert?.eventDate ||
                  pAlert?.startDate ||
                  ''
                : '',
          }
        })
      // Append to whatever is already in alerts.personalAlerts instead of
      // replacing it. state.data.alerts may still be the initial [] here.
      if (!state.data.alerts || Array.isArray(state.data.alerts)) {
        state.data.alerts = {}
      }
      state.data.alerts.personalAlerts = [
        ...(Array.isArray(state.data.alerts.personalAlerts)
          ? state.data.alerts.personalAlerts
          : []),
        ...portalPersonalAlerts,
      ]
      state.data.alerts.notificationAlerts = [
        ...(Array.isArray(state.data.alerts.notificationAlerts)
          ? state.data.alerts.notificationAlerts
          : []),
        ...portalPersonalAlerts,
      ].sort(
        (a, b) => (a?.acknowledgedTS ? 1 : 0) - (b?.acknowledgedTS ? 1 : 0),
      )
    },
    [acknowledgePortalAlerts.rejected]: (state) => {
      state.notificationAcknowledgeAlertsStatus = 'error'
    },
    [acknowledgePortalAlerts.pending]: (state) => {
      state.notificationAcknowledgeAlertsStatus = 'pending'
    },
    [acknowledgePortalAlerts.fulfilled]: (state, action) => {
      state.notificationAcknowledgeAlertsStatus = 'fulfilled'
      const universityAlertId = action.meta.arg
      const now = new Date().toISOString()

      // Announcements/Events: tracked via an `acknowledgements` array, split
      // into active/history lists based on its length.
      if (Array.isArray(state.data.notificationAlerts)) {
        state.data.notificationAlerts = state.data.notificationAlerts.map(
          (alert) =>
            alert?.id === universityAlertId
              ? {
                  ...alert,
                  acknowledgements: [
                    ...(Array.isArray(alert.acknowledgements)
                      ? alert.acknowledgements
                      : []),
                    { acknowledgedTS: now },
                  ],
                }
              : alert,
        )
        state.data.activeNotificationList =
          state.data.notificationAlerts.filter(
            (nAlert) => nAlert?.acknowledgements?.length === 0,
          )
        state.data.historyNotificationList =
          state.data.notificationAlerts.filter(
            (nAlert) => nAlert?.acknowledgements?.length > 0,
          )
      }

      // Other portal alert types (e.g. "Alert") flow through alerts.personalAlerts /
      // alerts.notificationAlerts and use `acknowledgedTS` instead.
      const markAcknowledgedTS = (alert) =>
        alert?.source === 'Notification' && alert?.dockey === universityAlertId
          ? { ...alert, acknowledgedTS: now }
          : alert
      if (Array.isArray(state.data.alerts?.personalAlerts)) {
        state.data.alerts.personalAlerts =
          state.data.alerts.personalAlerts.map(markAcknowledgedTS)
      }
      if (Array.isArray(state.data.alerts?.notificationAlerts)) {
        state.data.alerts.notificationAlerts =
          state.data.alerts.notificationAlerts
            .map(markAcknowledgedTS)
            .sort(
              (a, b) =>
                (a?.acknowledgedTS ? 1 : 0) - (b?.acknowledgedTS ? 1 : 0),
            )
      }
    },
  },
})

export const {
  focusAlert,
  widgetLoaded,
  resetRefresh,
  updateNotificationsDialogOpen,
  updateAlertIndex,
  updateAlertsHistoryOpen,
  updateSnackbarOpen,
  updateAlertTitle,
  updateMainTabValue,
  updateSecondaryTabValue,
} = myAlertsSlice.actions

export const selectMyAlertsData = (state) => state.myAlerts.data
export const selectMyAlertsStatus = (state) => state.myAlerts.status
export const selectMyAlertsRef = (state) => state.myAlerts.ref
export const selectMyAlertsRefresh = (state) => state.myAlerts.refresh
export const selectMyAlertsTitle = (state) => state.myAlerts.alertTitle

export default myAlertsSlice.reducer
